import React, { useEffect, useState } from 'react';
import { Drawer, Box, Typography, TextField, Button, IconButton, FormControl, InputLabel, Select, MenuItem, FormHelperText, useTheme, useMediaQuery, InputBase, InputAdornment, Divider, Tooltip } from '@mui/material';
import { XIcon, SearchIcon, InfoIcon } from 'lucide-react';
interface SubAgentUserFormProps {
  open: boolean;
  user: any | null;
  onClose: () => void;
  onSave: (userData: any) => void;
  preselectedSubAgent: string | null;
}
interface FormData {
  name: string;
  fatherName: string;
  cnic: string;
  email: string;
  phone: string;
  username: string;
  branchCode: string;
  settlementAccount: string;
  subAgent: string;
  userType: 'maker' | 'checker';
}
interface FormErrors {
  name?: string;
  fatherName?: string;
  cnic?: string;
  email?: string;
  phone?: string;
  username?: string;
  branchCode?: string;
  settlementAccount?: string;
  subAgent?: string;
  userType?: string;
}
const SUB_AGENTS = ['Islamabad Exchange', 'Karachi Money Transfer', 'Lahore Financial', 'Peshawar Remit', 'Quetta Exchange'];
const BRANCH_CODES = ['ISB001', 'ISB002', 'KHI001', 'KHI002', 'LHR001', 'LHR002', 'PSH001', 'QTA001'];
const SubAgentUserFormDrawer: React.FC<SubAgentUserFormProps> = ({
  open,
  user,
  onClose,
  onSave,
  preselectedSubAgent
}) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    fatherName: '',
    cnic: '',
    email: '',
    phone: '',
    username: '',
    branchCode: '',
    settlementAccount: '',
    subAgent: '',
    userType: 'maker'
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [subAgentSearchText, setSubAgentSearchText] = useState('');
  const [branchCodeSearchText, setBranchCodeSearchText] = useState('');
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        fatherName: user.fatherName || '',
        cnic: user.cnic || '',
        email: user.email || '',
        phone: user.phone || '',
        username: user.username || '',
        branchCode: user.branchCode || '',
        settlementAccount: user.settlementAccount || '',
        subAgent: user.subAgent || '',
        userType: user.userType || 'maker'
      });
    } else {
      setFormData({
        name: '',
        fatherName: '',
        cnic: '',
        email: '',
        phone: '',
        username: '',
        branchCode: '',
        settlementAccount: '',
        subAgent: preselectedSubAgent || '',
        userType: 'maker'
      });
    }
    setErrors({});
  }, [user, open, preselectedSubAgent]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | {
    name?: string;
    value: unknown;
  }>) => {
    const {
      name,
      value
    } = e.target;
    if (name) {
      setFormData({
        ...formData,
        [name]: value
      });
      // Clear error when field is edited
      if (errors[name as keyof FormErrors]) {
        setErrors({
          ...errors,
          [name]: undefined
        });
      }
    }
  };
  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (formData.cnic.trim() && !/^\d{5}-\d{7}-\d{1}$/.test(formData.cnic)) {
      newErrors.cnic = 'CNIC format should be 12345-1234567-1';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!/^\+92\d{10}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Phone must start with +92 and be exactly 10 digits after +92';
    }
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (!/^[a-z0-9.]+$/.test(formData.username)) {
      newErrors.username = 'Username can only contain lowercase letters, numbers, and dots';
    }
    if (!formData.branchCode) {
      newErrors.branchCode = 'Branch code is required';
    }
    if (!formData.settlementAccount.trim()) {
      newErrors.settlementAccount = 'Settlement account is required';
    }
    if (!formData.subAgent) {
      newErrors.subAgent = 'Sub Agent is required';
    }
    if (!formData.userType) {
      newErrors.userType = 'User Type is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = () => {
    if (validate()) {
      onSave({
        ...formData,
        id: user?.id
      });
    }
  };
  return <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{
    sx: {
      width: {
        xs: '100%',
        sm: 400
      },
      p: 0
    }
  }} data-id="element-2376" data-mp="c2nv38s8vc0q">
      <Box sx={{
      p: 3
    }} data-id="element-2377" data-mp="2pb6w0s4d5kgh">
        <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3
      }} data-id="element-2378" data-mp="2p9lyquqnznex">
          <Typography variant="h6" data-id="element-2379" data-mp="2yzymevtrzwpa">
            {user ? 'Edit User' : 'Add New User'}
          </Typography>
          <IconButton onClick={onClose} size="small" data-id="element-2380" data-mp="2u387ibtpj75g">
            <XIcon size={18} data-id="element-2381" data-mp="26c2ojnsgzyqx" />
          </IconButton>
        </Box>
        <Box component="form" noValidate autoComplete="off" sx={{
        mt: 2
      }} data-id="element-2382" data-mp="26ef5pyqem52x">
          <TextField fullWidth margin="normal" label="Name" name="name" value={formData.name} onChange={handleChange} error={!!errors.name} helperText={errors.name} required data-id="element-2383" data-mp="2fd98y7y8ptpg" />
          <TextField fullWidth margin="normal" label="Father Name" name="fatherName" value={formData.fatherName} onChange={handleChange} error={!!errors.fatherName} helperText={errors.fatherName} data-id="element-2384" data-mp="3mr3zx4c8ucwf" />
          <TextField fullWidth margin="normal" label="CNIC (Optional)" name="cnic" value={formData.cnic} onChange={handleChange} error={!!errors.cnic} helperText={errors.cnic || 'Format: 12345-1234567-1'} placeholder="12345-1234567-1" data-id="element-2385" data-mp="2tdl1yvx24jor" />
          <TextField fullWidth margin="normal" label="Email" name="email" type="email" value={formData.email} onChange={handleChange} error={!!errors.email} helperText={errors.email} required data-id="element-2386" data-mp="25m5t0karz44j" />
          <TextField fullWidth margin="normal" label="Phone" name="phone" value={formData.phone} onChange={handleChange} error={!!errors.phone} helperText={errors.phone || 'Must start with +92 and be 10 digits after +92'} required data-id="element-2387" data-mp="1m016awms457f" />
          <TextField fullWidth margin="normal" label="Username" name="username" value={formData.username} onChange={handleChange} error={!!errors.username} helperText={errors.username || 'Lowercase letters, numbers, and dots only'} required data-id="element-2388" data-mp="15hwqbq7bguej" />
          <FormControl fullWidth margin="normal" error={!!errors.branchCode} required data-id="element-2389" data-mp="lq050i48q40u">
            <InputLabel id="branch-code-label" data-id="element-2390" data-mp="cro5fqlebvw9">Branch Code</InputLabel>
            <Select labelId="branch-code-label" name="branchCode" value={formData.branchCode} onChange={handleChange} label="Branch Code" MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300
              },
              sx: {
                '& .MuiMenuItem-root': {
                  fontSize: '0.875rem'
                }
              }
            }
          }} data-id="element-2391" data-mp="7sbiz6yfsqu7">
              <MenuItem data-id="element-2392" data-mp="1cwyphyzr2863">
                <InputBase placeholder="Search branch codes..." startAdornment={<InputAdornment position="start" data-id="element-2394" data-mp="13xtomm0pu2qr">
                      <SearchIcon size={16} data-id="element-2395" data-mp="13zeo8q1w0bvh" />
                    </InputAdornment>} onClick={e => e.stopPropagation()} onChange={e => {
                setBranchCodeSearchText(e.target.value.toLowerCase());
              }} sx={{
                p: 1,
                width: '100%',
                fontSize: '0.875rem',
                '& .MuiInputBase-input': {
                  p: 0
                }
              }} data-id="element-2393" data-mp="1cwf94mpqzru6" />
              </MenuItem>
              <Divider data-id="element-2396" data-mp="pc65n469149h" />
              {BRANCH_CODES.filter(code => branchCodeSearchText === '' || code.toLowerCase().includes(branchCodeSearchText)).map(code => <MenuItem key={code} value={code} data-id="element-2397" data-mp="y9q8ghjrf8m7">
                  {code}
                </MenuItem>)}
            </Select>
            {errors.branchCode && <FormHelperText data-id="element-2398" data-mp="180b724zfod54">{errors.branchCode}</FormHelperText>}
          </FormControl>
          <TextField fullWidth margin="normal" label="Settlement Account (Branch/User)" name="settlementAccount" value={formData.settlementAccount} onChange={handleChange} error={!!errors.settlementAccount} helperText={errors.settlementAccount} required data-id="element-2399" data-mp="1331issns67iv" />
          <FormControl fullWidth margin="normal" error={!!errors.subAgent} required data-id="element-2400" data-mp="1be36zgpve0kz">
            <InputLabel id="sub-agent-label" data-id="element-2401" data-mp="16d77dfcjx0ld">Sub Agent</InputLabel>
            <Select labelId="sub-agent-label" name="subAgent" value={formData.subAgent} onChange={handleChange} label="Sub Agent" MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300
              },
              sx: {
                '& .MuiMenuItem-root': {
                  fontSize: '0.875rem'
                }
              }
            }
          }} data-id="element-2402" data-mp="11hi3izti0xj9">
              <MenuItem data-id="element-2403" data-mp="9p0vc7dav5x2">
                <InputBase placeholder="Search sub agents..." startAdornment={<InputAdornment position="start" data-id="element-2405" data-mp="2lde2phqq5wiu">
                      <SearchIcon size={16} data-id="element-2406" data-mp="2pdy0mnj4h461" />
                    </InputAdornment>} onClick={e => e.stopPropagation()} onChange={e => {
                setSubAgentSearchText(e.target.value.toLowerCase());
              }} sx={{
                p: 1,
                width: '100%',
                fontSize: '0.875rem',
                '& .MuiInputBase-input': {
                  p: 0
                }
              }} data-id="element-2404" data-mp="9ohoblp6u1xt" />
              </MenuItem>
              <Divider data-id="element-2407" data-mp="3d4bsoe1pj30m" />
              {SUB_AGENTS.filter(agent => subAgentSearchText === '' || agent.toLowerCase().includes(subAgentSearchText)).map(subAgent => <MenuItem key={subAgent} value={subAgent} data-id="element-2408" data-mp="1noce2hfbkldl">
                  {subAgent}
                </MenuItem>)}
            </Select>
            {errors.subAgent && <FormHelperText data-id="element-2409" data-mp="2m0ezwvsnu0g9">{errors.subAgent}</FormHelperText>}
          </FormControl>
          <FormControl fullWidth margin="normal" error={!!errors.userType} required data-id="element-2410" data-mp="3006lpipf4l4t">
            <InputLabel id="user-type-label" data-id="element-2411" data-mp="2v0bt9apzvnhn">User Type</InputLabel>
            <Select labelId="user-type-label" name="userType" value={formData.userType} onChange={handleChange} label="User Type" endAdornment={<Tooltip title="Select user's operational role (Maker creates, Checker reviews/approves)." placement="top" arrow data-id="element-2413" data-mp="3ipe8kbx5p48z">
                  <IconButton size="small" sx={{
              mr: 0.5,
              color: 'rgba(0, 0, 0, 0.54)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            }} onClick={e => e.stopPropagation()} data-id="element-2414" data-mp="3spnam6dtxjwa">
                    <InfoIcon size={16} data-id="element-2415" data-mp="17794oumc92r" />
                  </IconButton>
                </Tooltip>} data-id="element-2412" data-mp="39y443pkljnpi">
              <MenuItem value="maker" data-id="element-2416" data-mp="658vbinccoig">Maker</MenuItem>
              <MenuItem value="checker" data-id="element-2417" data-mp="3okt3lreum0vw">Checker</MenuItem>
            </Select>
            <FormHelperText data-id="element-2418" data-mp="3tbja57z0g9s6">
              {errors.userType || "Select user's operational role (Maker creates, Checker reviews/approves)."}
            </FormHelperText>
          </FormControl>
        </Box>
      </Box>
      <Box sx={{
      position: 'sticky',
      bottom: 0,
      backgroundColor: 'background.paper',
      p: 3,
      borderTop: '1px solid #E5E7EB'
    }} data-id="element-2419" data-mp="3jn1c2yi48gs8">
        <Box sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 2
      }} data-id="element-2420" data-mp="39mqzng3wnde4">
          <Button variant="outlined" onClick={onClose} data-id="element-2421" data-mp="32mgesekoljto">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} data-id="element-2422" data-mp="2xnlx5fyga1b5">
            Save
          </Button>
        </Box>
      </Box>
    </Drawer>;
};
export default SubAgentUserFormDrawer;